// frontend/src/hooks/useExternalIdentity.ts — a user's chat-account connection.
//
// Consumes /api/projects/:projectId/users/:userId/external-identities/:provider
// (gate: `users:manage`, bounded server-side by the caller's managed roles — the
// same delegation rule as every other act on a member).
//
// The connection STATE arrives on the user detail read, so there is no query hook
// here — only the three mutations. That keeps the detail page to one round trip and
// means the state and the rest of the user's record can never disagree.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { ExternalIdentityInvite, TestMessageResponse } from '@ems-app/schemas/users/telegram';
import { apiClient } from '../lib/apiClient.js';
import { usersKeys } from './useUsers.js';
import { refreshOnSettled } from '../lib/settled.js';

const base = (projectId: string, userId: string, provider: string) =>
  `/projects/${projectId}/users/${userId}/external-identities/${provider}`;

/** Mint an invite. Returns BOTH credentials — the surface shows the link and the
 *  spoken-aloud code together, because which one an admin needs depends on how they
 *  are about to reach the person. */
export function useIssueInvite(
  projectId: string,
  userId: string,
  provider = 'telegram',
): UseMutationResult<ExternalIdentityInvite, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post<ExternalIdentityInvite>(`${base(projectId, userId, provider)}/invite`);
      return r.data;
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: usersKeys.detail(projectId, userId) })),
  });
}

export function useRevokeInvite(
  projectId: string,
  userId: string,
  provider = 'telegram',
): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`${base(projectId, userId, provider)}/invite`);
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: usersKeys.detail(projectId, userId) })),
  });
}

/** Send the person a real test message. A MUTATION, not a query: it makes someone's
 *  phone buzz and it corrects the stored reachability — never on render. Resolves with
 *  a RESULT even when delivery fails, so the caller reports a status rather than an
 *  error toast. */
export function useTestMessage(
  projectId: string,
  userId: string,
  provider = 'telegram',
): UseMutationResult<TestMessageResponse, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post<TestMessageResponse>(`${base(projectId, userId, provider)}/test-message`);
      return r.data;
    },
    // Either outcome may have changed reachability — refetch so the badge reflects
    // what the send just proved, not what we believed before it.
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: usersKeys.detail(projectId, userId) })),
  });
}

export function useDisconnectIdentity(
  projectId: string,
  userId: string,
  provider = 'telegram',
): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(base(projectId, userId, provider));
    },
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: usersKeys.detail(projectId, userId) })),
  });
}
